import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 WHO MAY GIVE AND WHAT THEY ATTEST (Jesse, 2026-10-23 — step-5 fix 2) come from the server
// (backend utils/giftLetterRequirements, via GET /validate) — these are the fallbacks for a response without them. AFM v2
// restricts interested parties, not relationships, so every relationship stays and the donor attests they have no stake.
const RELATIONSHIPS = [
  'Parent', 'Grandparent', 'Sibling', 'Spouse/Partner',
  'Aunt/Uncle', 'Other Family Member', 'Employer', 'Other',
];
const ATTESTATION = 'I am not the seller, builder, developer, real estate agent, lender, or anyone else with a stake in '
  + 'this sale, and I am not affiliated with any of them.';

function DoorlyHeader() {
  return (
    <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-10">
      <div className="max-w-2xl mx-auto">
        <a href="https://www.godoorly.com" target="_blank" rel="noreferrer">
          <img
            src={`${process.env.PUBLIC_URL}/doorly_logo_black.png`}
            alt="Doorly"
            style={{ height: '28px', width: 'auto', display: 'block' }}
          />
        </a>
      </div>
    </header>
  );
}

function StepIndicator({ step }) {
  const steps = ['Sign Gift Letter', 'Upload Proof of Funds'];
  return (
    <div className="flex items-center gap-0 mb-8">
      {steps.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <React.Fragment key={n}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                done ? 'bg-green-500 text-white' : active ? 'bg-[#3a6ad9] text-white' : 'bg-gray-100 text-gray-400'
              }`}>
                {done ? '✓' : n}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${
                active ? 'text-[#3a6ad9]' : done ? 'text-green-600' : 'text-gray-400'
              }`}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-3 transition-colors ${done ? 'bg-green-400' : 'bg-gray-100'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function GiftLetter() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Validation
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [giftData, setGiftData] = useState(null);

  // Step: 1 | 2 | 'done'
  const [step, setStep] = useState(1);

  // Step 1 form
  const [giftAmount, setGiftAmount] = useState('');
  const [amountLocked, setAmountLocked] = useState(false);
  const [donorRelationship, setDonorRelationship] = useState('');
  const [donorAddress, setDonorAddress] = useState('');
  const [donorPhone, setDonorPhone] = useState('');
  const [signed, setSigned] = useState(false);
  const [notInterested, setNotInterested] = useState(false);   // the interested-party attestation (fix 2)
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [step1Errors, setStep1Errors] = useState({});
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);

  // Step 2
  const [proofFiles, setProofFiles] = useState([]);
  // AFM v2: the donor's statement is needed only if the gift has not been deposited yet ('yes' | 'no' | '').
  const [giftDeposited, setGiftDeposited] = useState('');
  const [draggingProof, setDraggingProof] = useState(false);
  const proofInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    axios.get(`${API_URL}/api/gift-letter/validate/${token}`)
      .then(r => {
        setGiftData(r.data);
        setValid(true);
        // Pre-populate form fields from saved data
        if (r.data.giftAmount) { setGiftAmount(String(r.data.giftAmount)); setAmountLocked(true); }
        if (r.data.donorRelationship) setDonorRelationship(r.data.donorRelationship);
        if (r.data.donorAddress) setDonorAddress(r.data.donorAddress);
        if (r.data.donorPhone) setDonorPhone(r.data.donorPhone);
      })
      .catch(err => {
        const d = err.response?.data || {};
        if (d.borrowerFirstName) setGiftData({ borrowerFirstName: d.borrowerFirstName });
      })
      .finally(() => setValidating(false));
  }, [token]);

  // ── Canvas helpers ────────────────────────────────────────────────────────
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
  };
  const startDraw = (e) => { isDrawing.current = true; lastPos.current = getPos(e, canvasRef.current); };
  const draw = (e) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#111827'; ctx.lineWidth = 2.5;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
    lastPos.current = pos;
    setSigned(true);
  };
  const stopDraw = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    // Save signature to state so it persists across step changes
    if (canvasRef.current) {
      setSignatureDataUrl(canvasRef.current.toDataURL('image/png'));
    }
  };
  const clearSignature = () => {
    canvasRef.current?.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setSigned(false);
    setSignatureDataUrl('');
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateStep1 = () => {
    const e = {};
    const amt = Number((giftAmount || '').replace(/,/g, ''));
    if (!giftAmount || isNaN(amt) || amt <= 0) e.giftAmount = 'Enter a valid gift amount.';
    if (!donorRelationship) e.donorRelationship = 'Select your relationship to the borrower.';
    if (!donorAddress.trim()) e.donorAddress = 'Your address is required.';
    if (String(donorPhone || '').replace(/\D/g, '').length < 10) e.donorPhone = 'A phone number is required.';
    if (!notInterested) e.notInterested = 'Please confirm you have no stake in this sale.';
    if (!signed) e.signed = 'Please draw your signature below.';
    setStep1Errors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validateStep1()) setStep(2); };

  // ── File helpers ──────────────────────────────────────────────────────────
  const addProofFiles = useCallback((newFiles) => {
    const allowed = ['.pdf', '.jpg', '.jpeg', '.png'];
    const valid = newFiles.filter(f => allowed.some(ext => f.name.toLowerCase().endsWith(ext)));
    setProofFiles(prev => [...prev, ...valid]);
  }, []);

  // ── PDF generation ────────────────────────────────────────────────────────
  const generatePdf = useCallback(() => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const sigDataUrl = signatureDataUrl || (canvasRef.current ? canvasRef.current.toDataURL('image/png') : '');
    const amountNum = Number((giftAmount || '0').replace(/,/g, ''));

    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('GIFT LETTER', 105, 22, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${today}`, 20, 36);
    doc.setDrawColor(200, 200, 200); doc.line(20, 42, 190, 42);

    let y = 52;
    const lines = [
      'To Whom It May Concern:',
      '',
      `I, ${giftData.donorName}, hereby certify that I have given or will give a gift`,
      `of $${amountNum.toLocaleString()} to ${giftData.borrowerFullName} to be applied`,
      'toward the purchase of their home.',
      '',
      'This gift does not need to be repaid and no repayment is expected or',
      'implied in any form whatsoever.',
      '',
      'Donor Information:',
      `  Name: ${giftData.donorName}`,
      `  Relationship to Borrower: ${donorRelationship}`,
      `  Address: ${donorAddress}`,
      `  Phone: ${donorPhone || 'N/A'}`,
      `  Email: ${giftData.donorEmail}`,
      '',
      // 🔑 AFM v2 (fix 2): the donor is not an interested party to the transaction.
      ...doc.splitTextToSize(`I certify: ${(giftData && giftData.attestation) || ATTESTATION}`, 170),
      '',
      `Borrower: ${giftData.borrowerFullName}`,
    ];

    doc.setFontSize(10);
    for (const line of lines) {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.text(line, 20, y); y += 6;
    }

    y += 8;
    if (y + 40 > 280) { doc.addPage(); y = 20; }
    doc.addImage(sigDataUrl, 'PNG', 20, y, 80, 20);
    y += 26;
    doc.setDrawColor(150, 150, 150); doc.line(20, y, 100, y);
    doc.text(`Donor: ${giftData.donorName}`, 20, y + 6);
    doc.text(`Date Signed: ${today}`, 20, y + 14);

    y += 28;
    doc.setFontSize(8); doc.setTextColor(160, 160, 160);
    doc.text('Digitally signed via Doorly | NMLS #2764749 | Equal Housing Opportunity', 105, y, { align: 'center' });

    return doc.output('blob');
  }, [giftAmount, donorRelationship, donorAddress, donorPhone, giftData, signatureDataUrl]);
  const relationships = (giftData && Array.isArray(giftData.relationships) && giftData.relationships.length)
    ? giftData.relationships : RELATIONSHIPS;

  // ── Submit ────────────────────────────────────────────────────────────────
  const statementNeeded = giftDeposited !== 'yes';
  const readyToSubmit = !!giftDeposited && (!statementNeeded || proofFiles.length > 0);
  const handleSubmit = async () => {
    if (!readyToSubmit) return;
    if (!token) {
      setSubmitError('Invalid gift letter link. Please use the link from your email.');
      return;
    }
    setSubmitting(true); setSubmitError('');

    console.log('[GiftLetter] Submit clicked', {
      token: token?.slice(0, 10) + '...',
      proofFiles: proofFiles.length,
      giftAmount,
      donorRelationship,
    });

    try {
      const pdfBlob = generatePdf();
      const dateStr = new Date().toISOString().slice(0, 10);
      const pdfFile = new File([pdfBlob], `Gift_Letter_Signed_${dateStr}.pdf`, { type: 'application/pdf' });

      console.log('[GiftLetter] PDF generated:', pdfFile.size, 'bytes');

      const fd = new FormData();
      fd.append('signed_letter', pdfFile);
      proofFiles.forEach(f => fd.append('files', f));
      fd.append('gift_amount', String(Number((giftAmount || '0').replace(/,/g, ''))));
      fd.append('donor_relationship', donorRelationship);
      fd.append('donor_address', donorAddress.trim());
      fd.append('donor_phone', donorPhone.trim());
      fd.append('gift_deposited', giftDeposited);
      fd.append('not_interested_party', notInterested ? 'yes' : 'no');

      console.log('[GiftLetter] Posting to:', `${API_URL}/api/gift-letter/submit/${token}`);

      const res = await axios.post(`${API_URL}/api/gift-letter/submit/${token}`, fd);
      console.log('[GiftLetter] Success:', res.data);
      setStep('done');
    } catch (err) {
      const serverError = err.response?.data?.error;
      const statusCode = err.response?.status;
      const detail = err.response?.data?.detail;

      console.error('[GiftLetter] Submit error:', {
        status: statusCode,
        serverError,
        detail,
        message: err.message,
        hasResponse: !!err.response,
      });

      if (serverError) {
        setSubmitError(serverError);
      } else if (statusCode) {
        setSubmitError(`Server error (${statusCode}). Please try again or contact support.`);
      } else if (err.message?.includes('Network Error')) {
        setSubmitError('Network error — please check your connection and try again.');
      } else {
        setSubmitError(`Submission failed: ${err.message}. Please try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (validating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#3a6ad9] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Invalid / expired token ───────────────────────────────────────────────
  if (!valid) {
    return (
      <div className="min-h-screen bg-gray-50">
        <DoorlyHeader />
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <span className="text-3xl">🔗</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Link Expired or Invalid</h1>
          <p className="text-gray-500 leading-relaxed mb-8">
            This gift letter link has expired or is no longer valid.
            {giftData?.borrowerFirstName
              ? ` Please ask ${giftData.borrowerFirstName} to send a new request.`
              : ' Please contact the person who sent you this link for a new one.'}
          </p>
          <a
            href="https://www.godoorly.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#3a6ad9] text-white font-semibold px-6 py-3 rounded-full hover:opacity-90 transition"
          >
            Go to Doorly →
          </a>
        </div>
        <footer className="text-center py-6">
          <p className="text-xs text-gray-300">Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity</p>
        </footer>
      </div>
    );
  }

  // ── Thank you page ────────────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gray-50">
        <DoorlyHeader />
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-green-50 border border-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Thank You, {giftData.donorName}! 🎉</h1>
          <p className="text-gray-600 leading-relaxed mb-3">
            Your gift letter has been signed{giftDeposited === 'yes' ? ' and submitted' : ' and your bank statement has been submitted'}.
          </p>
          <p className="text-gray-600 leading-relaxed mb-3">
            <strong>{giftData.borrowerFirstName}</strong> has been notified that everything is complete.
          </p>
          <p className="text-gray-600 leading-relaxed mb-8">
            Your generosity is helping make homeownership possible. Thank you!
          </p>
          <a
            href="https://www.godoorly.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-[#3a6ad9] font-medium hover:underline"
          >
            Learn more about Doorly →
          </a>
        </div>
        <footer className="text-center py-6">
          <p className="text-xs text-gray-400">Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity</p>
        </footer>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────
  const canProceed = giftAmount && donorRelationship && donorAddress.trim() && notInterested && signed;

  return (
    <div className="min-h-screen bg-gray-50">
      <DoorlyHeader />
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Page title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Gift Letter &amp; Proof of Funds</h1>
          <p className="text-gray-500 mt-2 text-sm">
            Please complete both steps below to support{' '}
            <strong>{giftData.borrowerFirstName}'s</strong> home purchase.
          </p>
        </div>

        <StepIndicator step={step} />

        {/* ── STEP 1 ─────────────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 pt-6 pb-4 border-b border-gray-50">
              <h2 className="text-base font-bold text-gray-900">Step 1 of 2 — Review &amp; Sign Gift Letter</h2>
              <p className="text-xs text-gray-500 mt-1">
                Review the letter below, fill in any required fields, and draw your signature.
              </p>
            </div>

            {/* Gift letter document card */}
            <div className="mx-6 my-6 bg-gray-50 border border-gray-200 rounded-xl p-6">
              <div className="text-center mb-5">
                <h3 className="text-xl font-bold text-gray-900 tracking-widest">GIFT LETTER</h3>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>

              <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                <p className="font-semibold">To Whom It May Concern:</p>

                {/* Gift amount inline */}
                <p>
                  I, <strong>{giftData.donorName}</strong>, hereby certify that I have given or will give a gift of{' '}
                  <span className="inline-flex items-center border-b border-gray-400 gap-1">
                    <span className="font-medium text-gray-900">$</span>
                    <input
                      type="text"
                      value={amountLocked && giftAmount ? Number(giftAmount).toLocaleString('en-US', { minimumFractionDigits: 2 }) : giftAmount}
                      onChange={amountLocked ? undefined : e => {
                        setGiftAmount(e.target.value.replace(/[^0-9.]/g, ''));
                        if (step1Errors.giftAmount) setStep1Errors(p => ({ ...p, giftAmount: '' }));
                      }}
                      readOnly={amountLocked}
                      placeholder="0.00"
                      className={`w-28 px-1 py-0.5 border rounded text-sm focus:outline-none ${amountLocked ? 'bg-gray-50 cursor-not-allowed font-semibold text-gray-700' : 'bg-white focus:ring-1 focus:ring-[#3a6ad9]'} ${step1Errors.giftAmount ? 'border-red-400' : 'border-gray-300'}`}
                    />
                  </span>{' '}
                  to <strong>{giftData.borrowerFullName}</strong> to be applied toward the purchase of their home.
                </p>
                {step1Errors.giftAmount && <p className="text-xs text-red-500 -mt-2">{step1Errors.giftAmount}</p>}

                <p>
                  This gift does not need to be repaid and <strong>no repayment is expected or implied</strong>{' '}
                  in any form whatsoever.
                </p>

                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <p className="font-semibold">Donor Information:</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Name</p>
                      <p className="font-medium">{giftData.donorName}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">
                        Relationship to Borrower <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={donorRelationship}
                        onChange={e => {
                          setDonorRelationship(e.target.value);
                          if (step1Errors.donorRelationship) setStep1Errors(p => ({ ...p, donorRelationship: '' }));
                        }}
                        className={`w-full h-8 px-2 border rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#3a6ad9] ${step1Errors.donorRelationship ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                      >
                        <option value="">Select…</option>
                        {relationships.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      {step1Errors.donorRelationship && <p className="text-xs text-red-500 mt-0.5">{step1Errors.donorRelationship}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={donorAddress}
                      onChange={e => {
                        setDonorAddress(e.target.value);
                        if (step1Errors.donorAddress) setStep1Errors(p => ({ ...p, donorAddress: '' }));
                      }}
                      placeholder="Street, City, State, ZIP"
                      className={`w-full h-8 px-2 border rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#3a6ad9] ${step1Errors.donorAddress ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    />
                    {step1Errors.donorAddress && <p className="text-xs text-red-500 mt-0.5">{step1Errors.donorAddress}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      {/* 🔑 REQUIRED, AND SAYS SO (Jesse, 2026-10-25 — Hannah's testing): it was checked on Next and on the
                          server, but the label read as optional. */}
                      <label htmlFor="gift-donor-phone" className="text-xs text-gray-500 mb-1 block">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="gift-donor-phone"
                        data-testid="gift-donor-phone"
                        type="tel"
                        required
                        aria-required="true"
                        value={donorPhone}
                        onChange={e => {
                          setDonorPhone(e.target.value);
                          if (step1Errors.donorPhone) setStep1Errors(p => ({ ...p, donorPhone: '' }));
                        }}
                        placeholder="(555) 000-0000"
                        className={`w-full h-8 px-2 border rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#3a6ad9] ${step1Errors.donorPhone ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                      />
                      {step1Errors.donorPhone && <p className="text-xs text-red-500 mt-0.5">{step1Errors.donorPhone}</p>}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Email</p>
                      <p className="text-sm font-medium">{giftData.donorEmail}</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-xs text-gray-500 mb-1">Borrower</p>
                    <p className="font-medium">{giftData.borrowerFullName}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 🔑 AFM v2 (fix 2): the donor attests they are not an interested party to the sale. */}
            <div className="mx-6 mb-4">
              <label className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed cursor-pointer">
                <input type="checkbox" data-testid="gift-not-interested-party" checked={notInterested}
                  onChange={(e) => setNotInterested(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0" />
                <span>{(giftData && giftData.attestation) || ATTESTATION} <span className="text-red-500">*</span></span>
              </label>
              {step1Errors.notInterested && <p className="text-xs text-red-500 mt-1">{step1Errors.notInterested}</p>}
            </div>

            {/* Signature pad */}
            <div className="mx-6 mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">
                  Donor Signature <span className="text-red-500">*</span>
                </label>
                <button onClick={clearSignature} className="text-xs text-gray-400 hover:text-red-500 transition">
                  Clear Signature
                </button>
              </div>
              <div className="relative">
                <canvas
                  ref={canvasRef}
                  width={800}
                  height={150}
                  className="w-full rounded-xl cursor-crosshair"
                  style={{
                    border: `1px solid ${step1Errors.signed ? '#ff6b84' : '#d1d5db'}`,
                    backgroundColor: '#fff',
                    touchAction: 'none',
                    borderRadius: '8px',
                  }}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={stopDraw}
                  onMouseLeave={stopDraw}
                  onTouchStart={e => { e.preventDefault(); startDraw(e); }}
                  onTouchMove={e => { e.preventDefault(); draw(e); }}
                  onTouchEnd={stopDraw}
                />
                {!signed && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="text-sm text-gray-400">Sign here using your mouse or finger</p>
                  </div>
                )}
              </div>
              {step1Errors.signed && <p className="text-xs text-red-500 mt-1">{step1Errors.signed}</p>}
            </div>

            <div className="px-6 pb-6">
              <button
                onClick={handleNext}
                title={!canProceed ? 'Please complete all fields and sign before continuing' : ''}
                className="w-full h-12 rounded-full font-semibold text-sm transition-all"
                style={{
                  backgroundColor: canProceed ? '#3a6ad9' : '#e5e7eb',
                  color: canProceed ? '#fff' : '#9ca3af',
                  cursor: canProceed ? 'pointer' : 'not-allowed',
                }}
              >
                Next: Upload Proof of Funds →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2 ─────────────────────────────────────────────────────── */}
        {step === 2 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 pt-6 pb-4 border-b border-gray-50">
              <h2 className="text-base font-bold text-gray-900">Step 2 of 2 — Proof of Funds</h2>
              <p className="text-xs text-gray-500 mt-1">
                Has the gift already been deposited into the borrower's account?
              </p>
              <div className="flex gap-2 mt-3" role="radiogroup" aria-label="Has the gift been deposited?">
                {[['yes', 'Yes, it has been deposited'], ['no', 'Not yet']].map(([v, label]) => (
                  <button key={v} type="button" role="radio" aria-checked={giftDeposited === v} data-testid={`gift-deposited-${v}`}
                    onClick={() => setGiftDeposited(v)}
                    className="h-9 px-4 rounded-full border text-sm font-medium transition"
                    style={{ borderColor: giftDeposited === v ? '#3a6ad9' : '#e5e7eb', backgroundColor: giftDeposited === v ? '#eef3fd' : '#fff', color: giftDeposited === v ? '#3a6ad9' : '#374151' }}>
                    {label}
                  </button>
                ))}
              </div>
              {giftDeposited === 'no' && (
                <p className="text-xs text-gray-500 mt-3">
                  Upload a bank statement showing the gift funds are available in your account.
                </p>
              )}
              {giftDeposited === 'yes' && (
                <p className="text-xs text-gray-500 mt-3">
                  No bank statement is needed. You can submit your signed letter now.
                </p>
              )}
            </div>

            {statementNeeded && giftDeposited && (
            <div className="px-6 pt-4 pb-2">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                <p className="text-xs font-semibold text-blue-700 mb-2">Requirements:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Your most recent statement showing the gift funds</li>
                  <li>• Must show your name and account balance</li>
                  <li>• All pages required even if blank</li>
                  <li>• PDF, JPG, or PNG accepted</li>
                </ul>
              </div>

              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); setDraggingProof(true); }}
                onDragLeave={() => setDraggingProof(false)}
                onDrop={e => { e.preventDefault(); setDraggingProof(false); addProofFiles(Array.from(e.dataTransfer.files)); }}
                onClick={() => proofInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  draggingProof ? 'border-[#3a6ad9] bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-[#3a6ad9]">Click to upload</span> or drag files here
                </p>
                <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG · Max 25 MB per file</p>
              </div>
              <input
                ref={proofInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={e => addProofFiles(Array.from(e.target.files))}
              />

              {proofFiles.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {proofFiles.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-sm">
                      <span className="text-base">{f.type === 'application/pdf' ? '📄' : '🖼️'}</span>
                      <span className="flex-1 truncate text-gray-700">{f.name}</span>
                      <span className="text-gray-400 text-xs">({(f.size / 1024).toFixed(0)} KB)</span>
                      <button
                        onClick={() => setProofFiles(p => p.filter((_, j) => j !== i))}
                        className="text-gray-400 hover:text-red-500 transition text-lg leading-none ml-1"
                      >×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}

            {submitError && (
              <div className="px-6 pb-2">
                <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              </div>
            )}

            <div className="px-6 pb-6 pt-2 flex gap-3">
              <button
                onClick={() => { setStep(1); setSubmitError(''); }}
                className="h-11 px-5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
              >
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={!readyToSubmit || submitting}
                className="flex-1 h-11 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-2"
                style={{
                  backgroundColor: readyToSubmit && !submitting ? '#3a6ad9' : '#e5e7eb',
                  color: readyToSubmit && !submitting ? '#fff' : '#9ca3af',
                  cursor: readyToSubmit && !submitting ? 'pointer' : 'not-allowed',
                }}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting…
                  </>
                ) : (
                  'Submit Gift Letter & Documents →'
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <footer className="text-center py-6">
        <p className="text-xs text-gray-300">Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity</p>
      </footer>
    </div>
  );
}
